import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Video, Loader2 } from 'lucide-react';
import EditorRico from '../../intervenciones/plantillas/EditorRico';

/**
 * EditorInstrucciones — el cuerpo del correo con el que se explica una credencial.
 *
 * REUTILIZA `EditorRico` de Intervenciones: un envoltorio de Quill con la barra limitada a lo
 * que sobrevive en un cliente de correo (cabeceras, énfasis, color, listas, alineación, enlaces).
 *
 * A aquel componente se le añadieron DOS COSAS, las dos aditivas y con valor por defecto que deja
 * Intervenciones exactamente igual: `formatosExtra` y `insertarHtmlEnCursor`. Hicieron falta
 * porque sin `image` en los formatos Quill BORRA el <img> de la miniatura, y porque el ref solo
 * exponía inserción de texto plano. El botón de vídeo sí vive aquí: es de este caso.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * POR QUÉ UNA MINIATURA Y NO UN VÍDEO
 *
 * Un `<video>` o un `<iframe>` de Vimeo **no se reproduce en Outlook ni en Gmail**: los clientes
 * de correo los eliminan. Lo que funciona es una imagen enlazada: se ve el fotograma, y al
 * pulsarlo se abre el vídeo en el navegador.
 *
 * La miniatura la sirve el propio Vimeo (`i.vimeocdn.com`), así que NO hay que subir nada a
 * ningún sitio: se pega el enlace del vídeo y su oEmbed devuelve la URL de la imagen.
 *
 * LÍMITE CONOCIDO Y ACEPTADO: esa URL lleva el hash del contenido. Si alguien cambia la
 * miniatura del vídeo en Vimeo, la URL deja de existir y en los correos ya enviados queda una
 * imagen rota — dentro de un enlace que **sigue funcionando**, porque el texto y el destino no
 * dependen de la imagen. Mantener un almacén propio de miniaturas no compensa ese riesgo.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * El saneador del servidor es la barrera real: acepta `img` SOLO con origen `i.vimeocdn.com`.
 * Este componente no puede relajarlo, y lo que aquí se cuele se descarta al guardar.
 */

// Acepta las formas habituales: vimeo.com/ID, player.vimeo.com/video/ID y enlaces con hash de
// vídeo privado (vimeo.com/ID/HASH).
const RE_VIMEO = /^https?:\/\/(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-z]+))?/i;

export default function EditorInstrucciones({ value, onChange, minHeight = 260 }) {
  const editorRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [url, setUrl] = useState('');
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);

  async function insertarVideo() {
    const enlace = url.trim();
    const m = RE_VIMEO.exec(enlace);
    if (!m) {
      toast.error('Pega un enlace de Vimeo (vimeo.com/… o player.vimeo.com/video/…).');
      return;
    }
    setCargando(true);
    try {
      // oEmbed público de Vimeo: devuelve la miniatura sin necesitar clave.
      const r = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(enlace)}&width=640`);
      if (!r.ok) throw new Error(`Vimeo respondió ${r.status}`);
      const datos = await r.json();
      if (!datos.thumbnail_url) throw new Error('Vimeo no devolvió miniatura para ese vídeo.');

      // Defensa en cliente del mismo criterio que aplica el servidor. Si algún día Vimeo sirviera
      // la miniatura desde otro host, mejor enterarse aquí que ver cómo el saneador la borra.
      const host = new URL(datos.thumbnail_url).hostname;
      if (host !== 'i.vimeocdn.com') {
        throw new Error(`La miniatura viene de ${host} y solo se admiten las de i.vimeocdn.com.`);
      }

      const titulo = (texto.trim() || datos.title || 'Ver el vídeo').replace(/"/g, '&quot;');
      // `display:block` y `max-width` en línea: en un correo no hay hoja de estilos.
      const html = `<p><a href="${enlace}" target="_blank" rel="noopener noreferrer">`
        + `<img src="${datos.thumbnail_url}" alt="${titulo}" width="${datos.thumbnail_width || 640}" `
        + `style="max-width:100%;height:auto;display:block"></a></p>`
        + `<p><a href="${enlace}" target="_blank" rel="noopener noreferrer">${titulo}</a></p>`;

      editorRef.current?.insertarHtmlEnCursor?.(html);
      setAbierto(false);
      setUrl('');
      setTexto('');
      toast.success('Miniatura insertada. El vídeo se abre al pulsarla.');
    } catch (e) {
      toast.error(e.message || 'No se pudo leer el vídeo de Vimeo.');
    } finally {
      setCargando(false);
    }
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-v25-md border border-v25-line px-2.5 py-1 text-xs text-v25-ink-700 hover:bg-v25-surface-sunken"
        >
          <Video className="h-3.5 w-3.5" />Insertar vídeo de Vimeo
        </button>
        <span className="text-[11px] text-v25-ink-400">
          Se inserta la miniatura enlazada: un vídeo incrustado no se reproduce en el correo.
        </span>
      </div>

      {abierto && (
        <div className="mb-2 space-y-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
          <input
            className="w-full rounded-v25-md border border-v25-line px-2.5 py-1.5 text-sm"
            placeholder="https://vimeo.com/1217589761"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
          />
          <input
            className="w-full rounded-v25-md border border-v25-line px-2.5 py-1.5 text-sm"
            placeholder="Texto del enlace (opcional; por defecto, el título del vídeo)"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={insertarVideo}
              disabled={cargando || !url.trim()}
              className="inline-flex items-center gap-1.5 rounded-v25-md bg-v25-brand-600 px-3 py-1 text-xs text-white disabled:opacity-50"
            >
              {cargando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}Insertar
            </button>
            <button
              type="button"
              onClick={() => { setAbierto(false); setUrl(''); setTexto(''); }}
              className="text-xs text-v25-ink-500 hover:text-v25-ink-800"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* `image` en los formatos: sin él Quill borra el <img> de la miniatura al insertarlo. */}
      <EditorRico ref={editorRef} value={value} onChange={onChange} minHeight={minHeight}
        formatosExtra={['image']}
        placeholder="Qué se le explica a la persona cuando reciba este acceso…" />
    </div>
  );
}
